import { useEffect, useRef } from 'react';
import { SVG } from '@svgdotjs/svg.js';

import useStore from '../../store/store';

const CircleGrid = () => {
	const gRef = useRef();

	const sourceHeight = useStore((state) => state.sourceHeight);
	const distance = useStore((state) => state.distance);
	const getSoundSourcePosition = useStore((state) => state.getSoundSourcePosition);
	const getSingleUnit = useStore((state) => state.getSingleUnit);
	const singleUnit = getSingleUnit();

	useEffect(() => {
		const draw = SVG('#grid');

		const gParent = draw.group();
		const { x, y } = getSoundSourcePosition();
		gParent.transform({ translateX: x, translateY: y });
		const g = gRef.current = gParent.group();

		const circleGroup = g.group();
		const textGroup = g.group();

		const oneMeter = (getSingleUnit() / 0.1) * 2;

		let unit = 1;
		if (distance > 25000) unit = 1000;
		else if (distance > 2500) unit = 100;
		else if (distance > 200) unit = 10;
		const showTextOnlyEvery10 = distance * 130 / 100 / unit > 80;

		for (let i = 0, count = 0; i <= distance * 130 / 100; i += unit, count++) {
			let circleStroke = 1, circleColor = '#f9f9f9';
			const isTenthCircle = count % 10 === 0;
			if (isTenthCircle) {
				circleStroke = 2;
				circleColor = '#fff';
			}
			circleGroup
				.circle(oneMeter * i).cx(0).cy(0)
				.attr({
					'stroke': circleColor,
					'stroke-width': circleStroke,
					'fill': 'none'
				});

			if (i !== 0 && (!showTextOnlyEvery10 || (showTextOnlyEvery10 && count % 10 === 0))) {
				const text1 = textGroup
					.text(`${Math.abs((oneMeter * i / 2) * 0.1 / singleUnit).toFixed(0)}m`)
					.attr({ y: -(oneMeter * i / 2 - 2) })
					.font({ anchor: 'middle', size:'10px' })
					.fill(isTenthCircle ? '#666' : '#999');

				const rectSize1 = text1.node.getBBox();
				textGroup
					.rect(rectSize1.width, rectSize1.height)
					.transform({ translate: { x: rectSize1.x, y: rectSize1.y }})
					.fill('#e2e7e6')
					.backward();

				const text2 = textGroup
					.text(`${Math.abs((oneMeter * i / 2) * 0.1 / singleUnit).toFixed(0)}m`)
					.attr({ y: oneMeter * i / 2 + 2 })
					.font({ anchor: 'middle', size:'10px' })
					.fill(isTenthCircle ? '#666' : '#999');

				const rectSize2 = text2.node.getBBox();
				textGroup
					.rect(rectSize2.width, rectSize2.height)
					.transform({ translate: { x: rectSize2.x, y: rectSize2.y }})
					.fill('#e2e7e6')
					.backward();
			}
		}
		return () => {
			gParent.remove();
		};
	}, [singleUnit]);

	useEffect(() => {
		gRef.current.transform({
			translateY: -sourceHeight * singleUnit / 0.1
		});
	}, [singleUnit, sourceHeight]);

	return null;
};

const SquareGrid = () => {
	const gRef = useRef();

	const sourceHeight = useStore((state) => state.sourceHeight);
	const distance = useStore((state) => state.distance);
	const getSoundSourcePosition = useStore((state) => state.getSoundSourcePosition);
	const getSingleUnit = useStore((state) => state.getSingleUnit);
	const getUnitFromMeter = useStore((state) => state.getUnitFromMeter);
	const singleUnit = getSingleUnit();

	useEffect(() => {
		const draw = SVG('#grid');

		const gParent = draw.group();
		const { x, y } = getSoundSourcePosition();
		gParent.transform({ translateX: x, translateY: y });
		const g = gRef.current = gParent.group();

		const linesGroup = g.group();
		const textGroup = g.group();

		const oneMeter = (getSingleUnit() / 0.1);

		let unit = 1;
		if (distance >= 60000) unit = 10000;
		else if (distance >= 6000) unit = 1000;
		else if (distance >= 750) unit = 100;
		else if (distance > 78) unit = 10;
		const showTextOnlyEvery10 = distance / unit > 30;

		for (let i = 0, count = 0; i <= distance; i += unit, count++) {
			let lineStroke = 1, lineColor = '#f9f9f9', dashArray = '2,4';
			const isTenthLine = count % 10 === 0;
			if (isTenthLine) {
				lineStroke = 2;
				lineColor = '#fff';
				dashArray = '1,2';
			}
			linesGroup
				.line(i * oneMeter, -getUnitFromMeter(distance), i * oneMeter, getUnitFromMeter(distance))
				.attr({
					'stroke': lineColor,
					'stroke-width': lineStroke,
					'stroke-dasharray': dashArray,
					'fill': 'none'
				});
			linesGroup
				.line(0, i * oneMeter, getUnitFromMeter(distance), i * oneMeter)
				.attr({
					'stroke': lineColor,
					'stroke-width': lineStroke,
					'stroke-dasharray': dashArray,
					'fill': 'none'
				});

			if (i !== 0) {
				linesGroup
					.line(0, -i * oneMeter, getUnitFromMeter(distance), -i * oneMeter)
					.attr({
						'stroke': lineColor,
						'stroke-width': lineStroke,
						'stroke-dasharray': dashArray,
						'fill': 'none'
					});
			}

			if (i !== 0 && (!showTextOnlyEvery10 || (showTextOnlyEvery10 && count % 10 === 0))) {
				const text1 = textGroup
					.text(`${Math.abs((oneMeter * i) * 0.1 / singleUnit).toFixed(0)}m`)
					.attr({ y: -(oneMeter * i - 3) })
					.font({ anchor: 'middle', size:'10px' })
					.fill(isTenthLine ? '#666' : '#999');

				const rectSize1 = text1.node.getBBox();
				textGroup
					.rect(rectSize1.width, rectSize1.height)
					.transform({ translate: { x: rectSize1.x, y: rectSize1.y }})
					.fill('#e2e7e6')
					.backward();

				const text2 = textGroup
					.text(`${Math.abs((oneMeter * i) * 0.1 / singleUnit).toFixed(0)}m`)
					.attr({ y: oneMeter * i + 2 })
					.font({ anchor: 'middle', size:'10px' })
					.fill(isTenthLine ? '#666' : '#999');

				const rectSize2 = text2.node.getBBox();
				textGroup
					.rect(rectSize2.width, rectSize2.height)
					.transform({ translate: { x: rectSize2.x, y: rectSize2.y }})
					.fill('#e2e7e6')
					.backward();

				const text3 = textGroup
					.text(`${Math.abs((oneMeter * i) * 0.1 / singleUnit).toFixed(0)}m`)
					.attr({ x: getUnitFromMeter(distance), y: -(oneMeter * i - 3) })
					.font({ anchor: 'middle', size:'10px' })
					.fill(isTenthLine ? '#666' : '#999');

				const rectSize3 = text3.node.getBBox();
				textGroup
					.rect(rectSize3.width, rectSize3.height)
					.transform({ translate: { x: rectSize3.x, y: rectSize3.y }})
					.fill('#e2e7e6')
					.backward();

				const text4 = textGroup
					.text(`${Math.abs((oneMeter * i) * 0.1 / singleUnit).toFixed(0)}m`)
					.attr({ x: getUnitFromMeter(distance), y: oneMeter * i + 2 })
					.font({ anchor: 'middle', size:'10px' })
					.fill(isTenthLine ? '#666' : '#999');

				const rectSize4 = text4.node.getBBox();
				textGroup
					.rect(rectSize4.width, rectSize4.height)
					.transform({ translate: { x: rectSize4.x, y: rectSize4.y }})
					.fill('#e2e7e6')
					.backward();

				const text5 = textGroup
					.text(`${Math.abs((oneMeter * i) * 0.1 / singleUnit).toFixed(0)}m`)
					.css('writing-mode', 'tb')
					.attr({ x: oneMeter * i, y: 0 })
					.font({ anchor: 'middle', size:'10px' })
					.fill(isTenthLine ? '#666' : '#999');

				const rectSize5 = text5.node.getBBox();
				textGroup
					.rect(rectSize5.width, rectSize5.height)
					.transform({ translate: { x: rectSize5.x, y: rectSize5.y }})
					.fill('#e2e7e6')
					.backward();
			}
		}
		return () => {
			gParent.remove();
		};
	}, [singleUnit]);

	useEffect(() => {
		gRef.current.transform({
			translateY: -sourceHeight * singleUnit / 0.1
		});
	}, [singleUnit, sourceHeight]);

	return null;
};

const Grid = () => {
	const grid = useStore((state) => state.grid);
	return (
		<>
			{grid === 'grid' && <SquareGrid />}
			{grid === 'distance' && <CircleGrid />}
		</>
	);
};

export { Grid };